import React, { useState } from "react";
import { Link } from "react-router-dom";
import { TaskCircle } from "./TaskCircle";
import { TaskMetaRow } from "./TaskMetaRow";
import { DragHandle } from "./DragHandle";
import { FiX, FiSend } from "react-icons/fi";
import { updateTask, addComment, createLabel, completeRecurringTask } from "../../firebase/tasks";
import { getAuth } from "firebase/auth";
import { describeRecurrence } from "../../utils/recurrenceEngine";
import { TaskTitleInput } from "./TaskTitleInput";
import { UserSelectDropdown } from "./UserSelectDropdown";
import { DatePicker } from "./DatePicker";
import { RecurrencePicker } from "./RecurrencePicker";
import { LabelTag } from "./LabelTag";

/**
 * TaskRow Component
 * 
 * Props:
 * - task: object (full task document)
 * - assigneeName: string (pre-resolved user name, optional)
 * - showCreatedAt: bool (optional)
 * - isTable: bool (renders as tr if true, otherwise li, default false)
 * - users: array (list of user objects for assignee select)
 * - allLabels: array (list of all label objects)
 * - setAllLabels: function (to refresh labels after create)
 * - currentUser: object (current authenticated user for audit log)
 * 
 * Renders:
 * - Left: DragHandle (revealed on hover) + TaskCircle (updates status to Resolved on click)
 * - Center: Title (navigates to detail page) + Description (if non-empty) + TaskMetaRow (date, comments, labels)
 * - Right: Hover reveal controls (Edit task tooltip/button + more menu button)
 */
export function TaskRow({ task, assigneeName, showCreatedAt = false, isTable = false, users = [], allLabels = [], setAllLabels = null, currentUser = null, onRecurrenceComplete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [hoverWarn, setHoverWarn] = useState(null);
  const [draft, setDraft] = useState({
    title: task.title || "",
    description: task.description || "",
    dueDate: task.dueDate || "",
    priority: task.priority || "Low",
    status: task.status || "Open",
    assigneeUid: task.assigneeUid || "",
    labels: task.labels || [],
    recurrence: task.recurrence || null
  });

  const handleCircleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const authorName = currentUser?.displayName || currentUser?.email || "System";
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

      const result = await completeRecurringTask(
        task.id,
        todayStr,
        currentUser?.uid || "system",
        authorName
      );

      if (result.advanced && onRecurrenceComplete) {
        onRecurrenceComplete({
          taskId: task.id,
          taskTitle: task.title,
          previousDueDate: result.previousDueDate,
          nextDueDate: result.nextDueDate,
        });
      }
    } catch (err) {
      console.error("Error completing task:", err);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!draft.title.trim()) return;

    try {
      const updates = {};
      const authorName = currentUser?.displayName || currentUser?.email || "System";
      const base = { authorUid: currentUser?.uid || "system", authorName: authorName, isSystem: true };

      const cleanTitle = draft.title.replace(/@\S+/g, '').replace(/\s+/g, ' ').trim();
      if (cleanTitle !== task.title) updates.title = cleanTitle;
      if (draft.description !== task.description) updates.description = draft.description.trim();
      if (draft.dueDate !== (task.dueDate || "")) {
        updates.dueDate = draft.dueDate || null;
        await addComment(task.id, {
          ...base,
          type: "date_change",
          text: `Due date changed from "${task.dueDate || "none"}" to "${draft.dueDate || "none"}" by ${authorName}`,
          oldValue: task.dueDate || null,
          newValue: draft.dueDate || null
        });
      }

      const oldLabels = task.labels || [];
      const newLabels = draft.labels || [];
      if (oldLabels.length !== newLabels.length || !oldLabels.every(l => newLabels.includes(l))) {
        updates.labels = newLabels;
        
        const auth = getAuth();
        const userUid = currentUser?.uid || auth.currentUser?.uid || "";
        for (const name of newLabels) {
          if (!oldLabels.includes(name)) {
            const existing = (allLabels || []).find(l => l.name.toLowerCase() === name.toLowerCase());
            createLabel(name, existing?.color || "#6b7280", userUid).catch(err => console.error("Error creating label in TaskRow:", err));
          }
        }
      }

      if (draft.priority !== task.priority) {
        updates.priority = draft.priority;
        await addComment(task.id, { ...base, type: "state_change", text: `Priority changed from "${task.priority}" to "${draft.priority}" by ${authorName}`, oldValue: task.priority, newValue: draft.priority });
      }
      
      if (draft.status !== task.status) {
        updates.status = draft.status;
        await addComment(task.id, { ...base, type: "state_change", text: `Status changed from "${task.status}" to "${draft.status}" by ${authorName}`, oldValue: task.status, newValue: draft.status });
      }

      if (draft.assigneeUid !== (task.assigneeUid || "")) {
        updates.assigneeUid = draft.assigneeUid || null;
        const getUserName = (uid) => {
          if (!uid) return "Unassigned";
          const matched = users.find((u) => u.uid === uid);
          return matched ? matched.name : "Unknown User";
        };
        const oldN = getUserName(task.assigneeUid);
        const newN = getUserName(draft.assigneeUid);
        await addComment(task.id, { ...base, type: "assignment_change", text: `Assignee changed from "${oldN}" to "${newN}" by ${authorName}`, oldValue: oldN, newValue: newN });
      }

      if (JSON.stringify(draft.recurrence || null) !== JSON.stringify(task.recurrence || null)) {
        updates.recurrence = draft.recurrence || null;
      }

      if (Object.keys(updates).length > 0) {
        updates.updatedByUid = currentUser?.uid || "system";
        await updateTask(task.id, updates);
      }
      setIsEditing(false);
    } catch (err) {
      console.error("Error updating task:", err);
    }
  };

  const handleCancel = () => {
    setDraft({
      title: task.title || "",
      description: task.description || "",
      dueDate: task.dueDate || "",
      priority: task.priority || "Low",
      status: task.status || "Open",
      assigneeUid: task.assigneeUid || "",
      labels: task.labels || [],
      recurrence: task.recurrence || null
    });
    setIsEditing(false);
  };

  const handleAddLabel = (labelName) => {
    const normalizedName = labelName.toLowerCase().trim();
    setDraft(prev => {
      const currentLabels = prev.labels || [];
      const alreadyAdded = currentLabels.some(l => l.toLowerCase() === normalizedName);
      if (!alreadyAdded) {
        return { ...prev, labels: [...currentLabels, normalizedName] };
      }
      return prev;
    });
    // subscribeToLabels in TasksDashboard auto-updates allLabels in real-time
  };

  const handleRemoveLabel = (labelName) => {
    setDraft(prev => ({ ...prev, labels: (prev.labels || []).filter(l => l !== labelName) }));
  };

  // Resolve assignee name if not passed
  let resolvedAssigneeName = assigneeName;
  if (!resolvedAssigneeName && task.assigneeUid && users.length > 0) {
    const matched = users.find(u => u.uid === task.assigneeUid);
    if (matched) resolvedAssigneeName = matched.name;
  }

  const renderHighlightedTitle = () => {
    if (!allLabels || allLabels.length === 0) return task.title;
    
    const words = (task.title || "").split(/(\s+)/);
    return words.map((word, i) => {
      if (word.startsWith("@")) {
        const potentialLabel = word.slice(1).toLowerCase();
        const isKnown = allLabels.some(l => l.name.toLowerCase() === potentialLabel);
        if (isKnown) {
          return (
            <span key={i} className="text-blue-600 bg-blue-50 px-0.5 rounded shadow-sm inline-block font-medium mx-px">
              {word}
            </span>
          );
        }
      }
      return <span key={i}>{word}</span>;
    });
  };

  const viewContent = (
    <>
      {/* Left: DragHandle & TaskCircle */}
      <div className="flex items-center gap-1.5 mt-0.5 shrink-0 select-none">
        <DragHandle />
        <TaskCircle
          status={task.status}
          priority={task.priority}
          onClick={handleCircleClick}
          isRecurring={!!task.recurrence?.isRecurring}
        />
      </div>

      {/* Center: Info area */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-col">
          {/* Title */}
          <Link
            to={`/tasks/${task.taskNumber}`}
            className="font-medium text-gray-800 hover:text-red-600 focus:outline-none focus:underline break-words text-sm cursor-pointer no-underline transition-colors duration-150"
          >
            <span className="text-gray-400 mr-1.5 font-medium select-none">#{task.taskNumber}</span>
            {renderHighlightedTitle()}
          </Link>
          
          {/* Metadata Row */}
          <TaskMetaRow
            dueDate={task.dueDate}
            commentCount={task.commentCount || 0}
            labels={task.labels}
            assigneeName={resolvedAssigneeName}
            priority={task.priority}
            status={task.status}
            recurrenceText={task.recurrence?.isRecurring ? describeRecurrence(task.recurrence) : ""}
          />
        </div>
      </div>

      {/* Right: Action controls (reveal on hover) */}
      <div className="flex items-center gap-1.5 shrink-0 self-center ml-2 relative visible md:invisible md:group-hover:visible focus-within:visible">
        <div className="relative group/edit">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIsEditing(true);
            }}
            className="p-1.5 hover:bg-gray-200 rounded text-gray-500 hover:text-gray-800 transition-colors duration-150 flex items-center justify-center cursor-pointer text-sm focus:outline-none border-none outline-none bg-transparent"
            aria-label="Edit task"
          >
            ✏️
          </button>
          {/* Custom Tooltip */}
          <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden md:group-hover/edit:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
            Inline Edit
          </span>
        </div>
      </div>
    </>
  );

  const editContent = (
    <div className="w-full">
      <form onSubmit={handleSave} className="border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm bg-white space-y-3 w-full">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 font-medium select-none text-sm shrink-0 pt-0.5">#{task.taskNumber}</span>
            <div className="flex-1">
              <TaskTitleInput
                value={draft.title}
                onChange={(val) => setDraft(prev => ({ ...prev, title: val }))}
                onAddLabel={handleAddLabel}
                allLabels={allLabels}
                autoFocus={true}
              />
            </div>
          </div>
          <textarea
            placeholder="Description"
            value={draft.description}
            onChange={(e) => setDraft(prev => ({ ...prev, description: e.target.value }))}
            className="w-full text-xs text-gray-500 placeholder-gray-400 border-none focus:ring-0 p-0 resize-none focus:outline-none mt-1 leading-relaxed"
            rows={2}
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-50">
          {/* Due Date Input */}
          <div 
            className="flex items-center gap-1 relative"
            onMouseEnter={() => setHoverWarn("dueDate")}
            onMouseLeave={() => setHoverWarn(null)}
            onFocus={() => setHoverWarn("dueDate")}
            onBlur={() => setHoverWarn(null)}
          >
            <span className="text-[10px] font-bold text-gray-400 uppercase">Due:</span>
            <DatePicker value={draft.dueDate} onChange={(val) => setDraft(prev => ({ ...prev, dueDate: val || "" }))} />
            {hoverWarn === "dueDate" && (
              <div className="absolute -top-6 left-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">
                Changes will be logged
              </div>
            )}
          </div>

          {/* Assignee Input */}
          <div 
            className="flex items-center gap-1 relative"
            onMouseEnter={() => setHoverWarn("assignee")}
            onMouseLeave={() => setHoverWarn(null)}
            onFocus={() => setHoverWarn("assignee")}
            onBlur={() => setHoverWarn(null)}
          >
            <span className="text-[10px] font-bold text-gray-400 uppercase">
              Assignee<span className="text-red-500">*</span>:
            </span>
            <UserSelectDropdown
              users={users}
              value={draft.assigneeUid}
              onChange={(val) => setDraft(prev => ({ ...prev, assigneeUid: val }))}
            />
            {hoverWarn === "assignee" && (
              <div className="absolute -top-6 left-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">
                Changes will be logged
              </div>
            )}
          </div>

          {/* Priority Input */}
          <div 
            className="flex items-center gap-1 relative"
            onMouseEnter={() => setHoverWarn("priority")}
            onMouseLeave={() => setHoverWarn(null)}
          >
            <span className="text-[10px] font-bold text-gray-400 uppercase">Priority:</span>
            <select
              value={draft.priority}
              onChange={(e) => setDraft(prev => ({ ...prev, priority: e.target.value }))}
              onFocus={() => setHoverWarn("priority")}
              onBlur={() => setHoverWarn(null)}
              className="border border-gray-200 rounded px-2 py-1 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-300"
            >
              <option value="Low">🟢 Low</option>
              <option value="Medium">🟡 Medium</option>
              <option value="High">🔴 High</option>
            </select>
            {hoverWarn === "priority" && (
              <div className="absolute -top-6 left-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">
                Changes will be logged
              </div>
            )}
          </div>
          
          {/* Status Input */}
          <div 
            className="flex items-center gap-1 relative"
            onMouseEnter={() => setHoverWarn("status")}
            onMouseLeave={() => setHoverWarn(null)}
          >
            <span className="text-[10px] font-bold text-gray-400 uppercase">Status:</span>
            <select
              value={draft.status}
              onChange={(e) => setDraft(prev => ({ ...prev, status: e.target.value }))}
              onFocus={() => setHoverWarn("status")}
              onBlur={() => setHoverWarn(null)}
              className="border border-gray-200 rounded px-2 py-1 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-gray-300"
            >
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="On Hold">On Hold</option>
              <option value="Closed">Closed</option>
            </select>
            {hoverWarn === "status" && (
              <div className="absolute -top-6 left-0 text-[10px] text-orange-500 bg-orange-50 px-1 border border-orange-100 rounded z-10 whitespace-nowrap shadow-sm pointer-events-none">
                Changes will be logged
              </div>
            )}
          </div>

          {/* Recurrence Input */}
          <div className="flex items-center gap-1">
            <RecurrencePicker
              value={draft.recurrence || null}
              onChange={(val) => setDraft(prev => ({ ...prev, recurrence: val }))}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          {/* Labels */}
          <div className="flex flex-wrap items-center gap-1.5">
            {draft.labels && draft.labels.length > 0 && draft.labels.map((labelName) => {
              const labelColor = allLabels.find(l => l.name.toLowerCase() === labelName.toLowerCase())?.color;
              return (
                <LabelTag 
                  key={labelName} 
                  name={labelName} 
                  color={labelColor}
                  onRemove={handleRemoveLabel} 
                />
              );
            })}
          </div>

          <div className="flex items-center gap-2 ml-auto shrink-0">
            <div className="relative group/cancel">
              <button
                type="button"
                onClick={handleCancel}
                className="w-8 h-8 flex items-center justify-center text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors duration-150 border-none outline-none focus:outline-none cursor-pointer"
                aria-label="Cancel"
              >
                <FiX className="w-4 h-4" />
              </button>
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/cancel:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
                Cancel
              </span>
            </div>
            
            <div className="relative group/save">
              <button
                type="submit"
                disabled={!draft.title.trim() || !draft.assigneeUid}
                className="w-8 h-8 flex items-center justify-center text-white bg-red-500 hover:bg-red-600 rounded-lg shadow-sm transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed border-none outline-none focus:outline-none cursor-pointer"
                aria-label="Save"
              >
                <FiSend className="w-4 h-4" />
              </button>
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/save:block bg-gray-800 text-white text-[10px] py-0.5 px-1.5 rounded shadow-sm whitespace-nowrap z-25 pointer-events-none">
                Save
              </span>
            </div>
          </div>
        </div>
      </form>
    </div>
  );

  if (isTable) {
    return (
      <tr className="group hover:bg-gray-50/80 transition-colors duration-150">
        <td colSpan={6} className="p-0">
          <div className={`flex items-start gap-3 py-2.5 px-2 sm:px-4 ${isEditing ? 'bg-gray-50/50' : ''}`}>
            {isEditing ? editContent : viewContent}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <li className={`group flex items-start gap-3 py-2.5 px-2 sm:px-4 hover:bg-gray-50/80 border-b border-gray-100 transition-colors duration-150 relative ${isEditing ? 'bg-gray-50/50' : ''}`}>
      {isEditing ? editContent : viewContent}
    </li>
  );
}
